import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Users, UserCheck, UserX, Shield, TrendingUp, Clock, type LucideIcon } from 'lucide-react'
import type { UserStats } from '@/modules/users/types/user'

interface StatsCardsProps {
  stats?: UserStats
  isLoading?: boolean
}

interface StatCardProps {
  title: string
  value: number | string
  icon: LucideIcon
  subtitle?: string
}

function StatCard({ title, value, icon: Icon, subtitle }: StatCardProps) {
  return (
    <Card className="p-6 border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <Icon className="h-6 w-6 text-teal-500" />
      </div>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card className="p-6 border border-gray-300 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-6 w-6 rounded" />
      </div>
    </Card>
  )
}

export function StatsCards({ stats, isLoading }: StatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!stats) {
    return null
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <StatCard
        title="Total Usuarios"
        value={stats.total_usuarios}
        icon={Users}
      />
      <StatCard
        title="Activos"
        value={stats.usuarios_activos}
        icon={UserCheck}
      />
      <StatCard
        title="Inactivos"
        value={stats.usuarios_inactivos}
        icon={UserX}
      />
      <StatCard
        title="Staff"
        value={stats.usuarios_staff}
        icon={Shield}
        subtitle={`${stats.usuarios_superuser} superusuarios`}
      />
      <StatCard
        title="Último Mes"
        value={stats.usuarios_ultimo_mes}
        icon={TrendingUp}
        subtitle="Nuevos usuarios"
      />
      <StatCard
        title="Última Semana"
        value={stats.usuarios_ultima_semana}
        icon={Clock}
        subtitle="Nuevos usuarios"
      />
    </div>
  )
}
